import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { TemplateType, TemplatesViewModel } from '../../../../extViewmodels';
import { ICreateSocialMediaPostRequest, ILocationState } from '../../../../models';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { toast } from 'sonner';
import { CreateGhostCalendarSuggestionViewModel } from '../../../../viewmodels/AdminViewModels';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { INavigationItemProps } from '../../../../web/containers/MainContainer';
import { EditGhostCalendarSuggestionStep } from './models';
import {
	GhostCalendarBlogSuggestion,
	GhostCalendarEmailSuggestion,
	GhostCalendarSocialSuggestion,
} from './presentation';
import { styleSheet } from './styles';

interface IProps extends INavigationItemProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const EditGhostCalendarSuggestion: React.FC<IProps> = observer(props => {
	const { className, styles = [], routeContainerClassName } = props;
	const { postNotification } = useLocalNotificationService();
	const { step } = useParams<{ step?: string }>();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const loc = useLocation<ILocationState<CreateGhostCalendarSuggestionViewModel, ICreateSocialMediaPostRequest>>();
	const match = useRouteMatch();
	const userSession = useUserSession();

	const createSuggestion = loc?.state?.viewModel;

	React.useEffect(() => {
		if (!createSuggestion?.template?.content && createSuggestion?.templateReference?.templateId) {
			new TemplatesViewModel(userSession)
				.getById(createSuggestion.templateReference.templateId)
				.then(template => {
					createSuggestion.template = template;
				})
				.catch(err => {
					errorMessages.pushApiError(err);
				});
		}
	}, [userSession, createSuggestion, errorMessages]);

	const onNext = React.useCallback(() => {
		const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> = { viewModel: createSuggestion };
		if (step === EditGhostCalendarSuggestionStep.Tags) {
			fullscreenModal?.history.push({
				pathname: `${match.url.replace(
					`/${EditGhostCalendarSuggestionStep.Tags}`,
					`/${EditGhostCalendarSuggestionStep.SendOptions}`
				)}`,
				state: locationState,
			});
			return;
		}

		fullscreenModal?.history.push({
			pathname: `${match.url.replace(
				`/${EditGhostCalendarSuggestionStep.Subverticals}`,
				`/${EditGhostCalendarSuggestionStep.Tags}`
			)}`,
			state: locationState,
		});
	}, [createSuggestion, fullscreenModal?.history, match.url, step]);

	const onNextBlogs = React.useCallback(() => {
		const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> = { viewModel: createSuggestion };
		if (step === EditGhostCalendarSuggestionStep.Subverticals) {
			fullscreenModal?.history.push({
				pathname: `${match.url.replace(
					`/${EditGhostCalendarSuggestionStep.Subverticals}`,
					`/${EditGhostCalendarSuggestionStep.SendOptions}`
				)}`,
				state: locationState,
			});
		}
	}, [createSuggestion, fullscreenModal?.history, match.url, step]);

	const onNextSocial = React.useCallback(() => {
		const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> = { viewModel: createSuggestion };
		if (step === EditGhostCalendarSuggestionStep.Targets) {
			fullscreenModal?.history.push({
				pathname: `${match.url.replace(
					`/${EditGhostCalendarSuggestionStep.Targets}`,
					`/${EditGhostCalendarSuggestionStep.SendOptions}`
				)}`,
				state: locationState,
			});
			return;
		}

		fullscreenModal?.history.push({
			pathname: `${match.url.replace(
				`/${EditGhostCalendarSuggestionStep.Subverticals}`,
				`/${EditGhostCalendarSuggestionStep.Targets}`
			)}`,
			state: locationState,
		});
	}, [createSuggestion, fullscreenModal?.history, match.url, step]);

	const onSave = React.useCallback(() => {
		createSuggestion
			?.update()
			.then(() => {
				fullscreenModal?.history.goBack();
				toast.success('Suggestion updated');
				postNotification({ topic: Topics.GHOST_CAL_UPDATE });
			})
			.catch(err => {
				errorMessages.pushApiError(err);
			});
	}, [createSuggestion, errorMessages, fullscreenModal?.history, postNotification]);

	const canGoNext = React.useMemo(() => {
		if (step === EditGhostCalendarSuggestionStep.Subverticals) {
			const isAllSubverticalsSelected =
				createSuggestion?.industries?.length !== 1 ||
				createSuggestion?.subverticals?.length === createSuggestion?.subverticalOptions?.length;
			return (
				createSuggestion?.industries?.length > 0 &&
				(isAllSubverticalsSelected || createSuggestion?.subverticals?.length > 0) &&
				createSuggestion?.accountAges?.length > 0
			);
		}
		return true;
	}, [step, createSuggestion]);

	return (
		<div
			className={`${css(
				createSuggestion?.template?.templateType === TemplateType.SocialMediaPost
					? styleSheet.containerSocial
					: styleSheet.container,
				...styles
			)} edit-ghost-calendar-suggestion ${className || ''} ${routeContainerClassName || ''}`}
		>
			<MultiContainerHeader
				fullscreenHeader={
					createSuggestion?.template?.templateType === TemplateType.Blog ? 'View Blog Post Prompt' : 'View Campaign'
				}
			/>
			{createSuggestion?.template?.templateType === TemplateType.SocialMediaPost ? (
				<GhostCalendarSocialSuggestion
					canGoNext={canGoNext}
					createSuggestion={createSuggestion}
					onNext={onNextSocial}
					onSave={onSave}
				/>
			) : createSuggestion?.template?.templateType === TemplateType.Blog ? (
				<GhostCalendarBlogSuggestion
					canGoNext={canGoNext}
					createSuggestion={createSuggestion}
					onNext={onNextBlogs}
					onSave={onSave}
				/>
			) : (
				<GhostCalendarEmailSuggestion
					canGoNext={canGoNext}
					createSuggestion={createSuggestion}
					onNext={onNext}
					onSave={onSave}
				/>
			)}
		</div>
	);
});
